import waitForElement from './wait-for-element';

// Configuration
const CONFIG = {
  navModule: '@tebra/navigation-ui',
  importMapUrl: 'https://cdn.prd.cloud.tebra.com/web-app-v2/importMap.json',
  systemJsUrl: 'https://cdn.jsdelivr.net/npm/systemjs@6.8.3/dist/system.min.js',
  systemJsAmdUrl: 'https://cdn.jsdelivr.net/npm/systemjs@6.8.3/dist/extras/amd.min.js',
  queryCoreModule: '@tanstack/query-core',
  queryCoreFallbackUrl: 'https://cdn.jsdelivr.net/npm/@tanstack/query-core@5.90.8/build/modern/index.js',
  importRetryAttempts: 2,
  importRetryDelayMs: 1000,
  navPendingClass: 'navigation-v2-pending',
  legacyNavCompiledAttr: 'data-nav2-legacy-compiled',
  legacyNavCompileAttempts: 200,
  legacyNavCompileIntervalMs: 50,
  navRenderWaitTimeoutMs: 1500,
};

// Module state
let systemJsLoadPromise = null;
let queryCoreLoadPromise = null;
let cachedQueryClient = null;
let pageIsLeaving = false;
const moduleImportPromises = new Map();

// Page lifecycle
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => { pageIsLeaving = true; });
  window.addEventListener('pageshow', () => { pageIsLeaving = false; });
  window.addEventListener('beforeunload', () => { pageIsLeaving = true; }, { once: true });
}

function createPageLeavingError() {
  const error = new Error('Navigation-v2 import cancelled during page teardown');
  error.name = 'NavigationV2PageLeavingError';
  return error;
}

// Utilities
const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

function importModuleCached(moduleName) {
  if (!moduleImportPromises.has(moduleName)) {
    const promise = window.System.import(moduleName).catch(error => {
      moduleImportPromises.delete(moduleName);
      throw error;
    });
    moduleImportPromises.set(moduleName, promise);
  }
  return moduleImportPromises.get(moduleName);
}

async function importWithRetry(moduleName, retries = CONFIG.importRetryAttempts, delayMs = CONFIG.importRetryDelayMs) {
  let lastError = null;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    if (pageIsLeaving) throw createPageLeavingError();

    try {
      return await importModuleCached(moduleName);
    } catch (error) {
      if (pageIsLeaving) throw createPageLeavingError();
      lastError = error;
      if (attempt < retries) await sleep(delayMs);
    }
  }

  throw lastError;
}

// Query Client
async function loadQueryCore() {
  if (queryCoreLoadPromise) return queryCoreLoadPromise;
  if (!window.System?.import) return null;

  queryCoreLoadPromise = (async () => {
    try {
      // Try the import map once. If query-core is not mapped, this will fail
      // deterministically and we should fall through to the CDN URL instead of retrying.
      return await window.System.import(CONFIG.queryCoreModule);
    } catch {
      // Shim process.env for browser-incompatible builds
      globalThis.process = globalThis.process || {};
      globalThis.process.env = globalThis.process.env || {};
      globalThis.process.env.NODE_ENV = globalThis.process.env.NODE_ENV || 'production';

      // Fallback to CDN if SystemJS import map doesn't have query-core
      // eslint-disable-next-line no-new-func
      return new Function('url', 'return import(url)')(CONFIG.queryCoreFallbackUrl);
    }
  })();

  return queryCoreLoadPromise;
}

async function getQueryClient() {
  if (cachedQueryClient) return cachedQueryClient;

  try {
    const mod = await loadQueryCore();
    const exports = mod?.default || mod;
    if (exports?.QueryClient && exports?.QueryCache) {
      cachedQueryClient = new exports.QueryClient({ queryCache: new exports.QueryCache() });
    }
  } catch {
    // Fallback to legacy nav handled by caller
  }

  return cachedQueryClient;
}

// DOM Utilities
function onDomReady() {
  if (document.readyState !== 'loading') return Promise.resolve();
  return new Promise(resolve => document.addEventListener('DOMContentLoaded', resolve, { once: true }));
}

function loadScript(src) {
  if (document.querySelector(`script[src="${src}"]`)) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.onload = resolve;
    script.onerror = () => reject(new Error(`Failed to load "${src}"`));
    document.head.appendChild(script);
  });
}

async function ensureImportMap(url) {
  if (document.querySelector('script[type="systemjs-importmap"]')) return;

  const script = document.createElement('script');
  script.type = 'systemjs-importmap';
  script.src = url;
  script.async = false;

  await new Promise((resolve, reject) => {
    script.onload = resolve;
    script.onerror = () => reject(new Error(`Failed to load import map from "${url}"`));
    document.head.appendChild(script);
  });
}

async function ensureSystemJs({ importMapUrl, systemJsUrl, systemJsAmdUrl }) {
  if (!systemJsLoadPromise) {
    systemJsLoadPromise = (async () => {
      await ensureImportMap(importMapUrl);
      await loadScript(systemJsUrl);
      if (systemJsAmdUrl) await loadScript(systemJsAmdUrl);
    })();
  }

  await systemJsLoadPromise;

  if (!window.System?.import) {
    throw new Error('SystemJS failed to initialize');
  }
}

// Legacy Navigation (Angular)
function createLegacyNavElement(selector, mountSelector) {
  const existing = document.querySelector(selector);
  if (existing) return existing;

  const mountEl = document.querySelector(mountSelector);
  if (!mountEl) return null;

  const legacyEl = document.createElement('div');
  const legacyId = selector.startsWith('#') ? selector.slice(1) : null;
  if (legacyId) legacyEl.id = legacyId;
  legacyEl.setAttribute('chrome-header', '');
  legacyEl.style.display = 'none';
  mountEl.insertAdjacentElement('afterend', legacyEl);

  return legacyEl;
}

function tryCompileLegacyNavElement(el) {
  if (!el || el.getAttribute(CONFIG.legacyNavCompiledAttr) === 'true') return true;

  const angularApi = window.angular;
  if (!angularApi?.element) return false;

  const rootEl = document.querySelector('[ng-app]') || document.body || document.documentElement;
  const rootInjector = angularApi.element(rootEl).injector?.();
  if (!rootInjector) return false;

  try {
    const $compile = rootInjector.get('$compile');
    const $rootScope = rootInjector.get('$rootScope');
    $compile(el)($rootScope);
    $rootScope.$evalAsync(() => {});
    el.setAttribute(CONFIG.legacyNavCompiledAttr, 'true');
    return true;
  } catch {
    return false;
  }
}

function ensureLegacyNavReady(selector, mountSelector) {
  const el = createLegacyNavElement(selector, mountSelector);
  if (!el) return null;
  if (tryCompileLegacyNavElement(el)) return el;

  let attempts = 0;
  const timer = setInterval(() => {
    attempts += 1;
    if (tryCompileLegacyNavElement(el) || attempts >= CONFIG.legacyNavCompileAttempts) {
      clearInterval(timer);
    }
  }, CONFIG.legacyNavCompileIntervalMs);

  return el;
}

function setLegacyNavVisibility(selector, visible, mountSelector) {
  let el = document.querySelector(selector);
  if (visible && !el) {
    el = ensureLegacyNavReady(selector, mountSelector);
  }
  if (el) el.style.display = visible ? 'block' : 'none';
}

export function showLegacyNavigation(mountSelector = '#nav-v2', legacySelector = '#chrome') {
  setLegacyNavVisibility(legacySelector, true, mountSelector);
}

export function hideLegacyNavigation(mountSelector = '#nav-v2', legacySelector = '#chrome') {
  setLegacyNavVisibility(legacySelector, false, mountSelector);
}

function scheduleLegacyNavigationPrime(mountSelector, legacySelector) {
  const schedule = window.requestIdleCallback || (cb => setTimeout(cb, 0));
  schedule(() => ensureLegacyNavReady(legacySelector, mountSelector));
}

// Nav Content Detection
function setNavPendingState(enabled) {
  document.documentElement.classList.toggle(CONFIG.navPendingClass, !!enabled);
}

function hasMountedNavContent(root) {
  if (!root || !document.documentElement.contains(root)) return false;
  if (root.childElementCount === 0) return false;
  if (!root.querySelector('.MuiToolbar-root')) return false;

  const rect = root.getBoundingClientRect();
  return !!rect && rect.height > 0;
}

function waitForMountedNavContent(root, timeoutMs = CONFIG.navRenderWaitTimeoutMs) {
  if (hasMountedNavContent(root)) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      observer.disconnect();
      reject(new Error('Navigation-v2 mount completed without rendering visible nav content'));
    }, timeoutMs);

    const observer = new MutationObserver(() => {
      if (!hasMountedNavContent(root)) return;
      observer.disconnect();
      clearTimeout(timeout);
      resolve();
    });

    observer.observe(root, { childList: true, subtree: true, attributes: true });
  });
}

// Nav Module Mounting
async function mountNavModule(navModule, navProps, { bootstrap = true } = {}) {
  if (bootstrap && navModule?.bootstrap) {
    await navModule.bootstrap(navProps);
  }
  await navModule.mount(navProps);
}

export async function remountNavigationUi(handle, mountElement) {
  if (!handle?.mounted || !handle?.navModule?.mount || !mountElement) return handle;

  setNavPendingState(true);
  try {
    // Unmount from old element (may fail if element was removed)
    try {
      await handle.unmount?.();
    } catch {
      // Continue - old element may already be gone
    }

    const navProps = { ...handle.navProps, domElement: mountElement };

    // Try remount without bootstrap first, fall back to full bootstrap
    try {
      await mountNavModule(handle.navModule, navProps, { bootstrap: false });
    } catch {
      await mountNavModule(handle.navModule, navProps, { bootstrap: true });
    }

    await waitForMountedNavContent(mountElement);

    return {
      ...handle,
      navProps,
      unmount: async () => handle.navModule.unmount?.(navProps),
    };
  } finally {
    setNavPendingState(false);
  }
}

export async function mountNavigationUiWithFallback(options = {}) {
  const {
    moduleName = CONFIG.navModule,
    importMapUrl = CONFIG.importMapUrl,
    systemJsUrl = CONFIG.systemJsUrl,
    systemJsAmdUrl = CONFIG.systemJsAmdUrl,
    mountSelector = '#nav-v2',
    legacySelector = '#chrome',
    logger = null,
    shouldMount = null,
    onMountSuccess = () => {},
    onMountSkipped = () => {},
    onMountFailure = () => {},
  } = options;

  const skippedResult = {
    mounted: false,
    navModule: null,
    navProps: null,
    unmount: async () => {},
  };

  try {
    // Start parallel work
    const domReadyPromise = onDomReady();
    const shouldMountPromise = typeof shouldMount === 'function' ? shouldMount() : Promise.resolve(true);
    const systemJsPromise = ensureSystemJs({ importMapUrl, systemJsUrl, systemJsAmdUrl });

    // Check feature toggle (can resolve before DOM ready)
    const canMount = await shouldMountPromise;
    if (!canMount) {
      await domReadyPromise;
      showLegacyNavigation(mountSelector, legacySelector);
      setNavPendingState(false);
      onMountSkipped();
      return skippedResult;
    }

    await systemJsPromise;

    if (pageIsLeaving) throw createPageLeavingError();

    // Load React first to prevent react-dom initialization race
    await importWithRetry('react');

    // Load nav module and query client in parallel
    const navModulePromise = importWithRetry(moduleName);
    const queryClientPromise = moduleName === CONFIG.navModule ? getQueryClient() : Promise.resolve(null);

    const [navModule] = await Promise.all([navModulePromise, domReadyPromise]);

    if (!navModule?.bootstrap || !navModule?.mount) {
      throw new Error(`Module "${moduleName}" missing bootstrap/mount lifecycles`);
    }

    const mountElement = await waitForElement(mountSelector);
    const queryClient = await queryClientPromise;

    if (moduleName === CONFIG.navModule && !queryClient) {
      throw new Error('Unable to initialize queryClient for @tebra/navigation-ui');
    }

    const navProps = { name: moduleName, domElement: mountElement, logger };
    if (queryClient) navProps.queryClient = queryClient;

    await mountNavModule(navModule, navProps);
    await waitForMountedNavContent(mountElement);

    hideLegacyNavigation(mountSelector, legacySelector);
    setNavPendingState(false);
    scheduleLegacyNavigationPrime(mountSelector, legacySelector);
    onMountSuccess();

    return {
      mounted: true,
      navModule,
      navProps,
      unmount: async () => navModule.unmount?.(navProps),
    };
  } catch (error) {
    const isQueryClientError = error?.message?.includes('Unable to initialize queryClient');
    const isPageLeavingError = error?.name === 'NavigationV2PageLeavingError';

    if (!isQueryClientError && !isPageLeavingError) {
      logger?.error?.(`Failed to load ${moduleName}; falling back to legacy nav`, error);
      onMountFailure(error);
    } else if (!isPageLeavingError) {
      onMountSkipped();
    }

    showLegacyNavigation(mountSelector, legacySelector);
    setNavPendingState(false);
    return { ...skippedResult, error };
  }
}
